import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Auth } from '@freelancer/auth';
import { Location } from '@freelancer/location';
import { Pwa } from '@freelancer/pwa';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import { payPalNativeCheckoutAuthPlugin } from './capacitor-plugin';
import type { PayPalNativeCheckoutAuthResponse } from './paypal-native-checkout-auth.model';

@UntilDestroy({ className: 'PayPalNativeCheckoutAuth' })
@Injectable({
  providedIn: 'root',
})
export class PayPalNativeCheckoutAuth {
  constructor(
    private auth: Auth,
    private location: Location,
    private pwa: Pwa,
  ) {}

  async startNativeCheckoutAuth(
    url: string,
  ): Promise<PayPalNativeCheckoutAuthResponse | undefined> {
    const candUseNativeCheckoutAuth = await this.canUseNativeCheckoutAuth();
    if (!candUseNativeCheckoutAuth) {
      throw new Error(
        'Native checkout is not supported on this platform or app version.',
      );
    }

    try {
      const authState = await firstValueFrom(
        this.auth.authState$.pipe(untilDestroyed(this)),
      );
      const authedRedirectUrl = authState
        ? // redirect to the link bypass first to prevent app link then redirect to internal auth callback page
          // to auth the user to FLN and then redirect to the checkout URL via the internal checkout redirect route
          `${
            this.location.origin
          }/internal/mobile-link-bypass-redirect?next=${encodeURIComponent(
            `/internal/auth/callback?uid=${
              authState.userId
              // encode the token twice to prevent the token from being decoded by the browser
            }&token=${encodeURIComponent(
              encodeURIComponent(authState.token),
            )}&next=${encodeURIComponent(
              // encode the checkout URL twice to prevent the URL from being decoded by the browser
              // until the last redirect to the checkout URL
              `/deposit/paypal-native-checkout-redirect?checkoutUrl=${encodeURIComponent(
                encodeURIComponent(url),
              )}`,
            )}`,
          )}&ngsw-bypass=`
        : url;
      const result = await payPalNativeCheckoutAuthPlugin.startCheckoutAuth({
        url: authedRedirectUrl,
      });
      return result;
    } catch (e) {
      // User has cancelled the flow by clicking on the close button
      return undefined;
    }
  }

  canUseNativeCheckoutAuth(): boolean {
    // Native paypal checkout auth is only supported on native apps
    if (this.pwa.isNative() && this.pwa.getPlatform() === 'android') {
      return Capacitor.isPluginAvailable('PayPalCheckoutAuth');
    }
    return false;
  }
}
